import { HttpParams } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 LazyDataPaging class handles the pagination and loading of data in a lazy manner.
 It takes a query function and optional a mapper function as inputs and uses
 them to perform the data loading and mapping.
 @class LazyDataPaging
 */
class LazyDataPaging {
    ref;
    /**
   Function that takes the event and HttpParams as input, and returns an observable.
   The observable is used to perform the data loading.
   @type {(event: any, params: HttpParams) => Observable<any>}
   @memberof LazyDataPaging
   */
    query;
    /**
   Function that takes the input data and maps it to a Page object.
   @type {(input: any) => Page<any>}
   @memberof LazyDataPaging
   */
    mapper = (input) => input;
    options;
    /**
   BehaviorSubject that holds the current data.
   @type {BehaviorSubject<any>}
   @memberof LazyDataPaging
   */
    data = new BehaviorSubject([]);
    /**
   Total number of records for the current query.
   @type {*}
   @memberof LazyDataPaging
   */
    totalRecords;
    /**
   Boolean flag that indicates if data is currently being loaded.
   @type {boolean}
   @memberof LazyDataPaging
   */
    loading = false;
    /**
   The last LazyLoadEvent that was used to load data.
   @type {(LazyLoadEvent | undefined)}
   @memberof LazyDataPaging
   */
    lastLazyLoad;
    changeRef;
    constructor(ref) {
        this.ref = ref;
        this.changeRef = ref;
    }
    /**
   Loads data using the provided query function and prepares query parameters
   using the event. Subscribes to the result and maps it to a Page object.
   Then, it sets the new data, total number of records and loading status.
   Finally, it marks the change detection for check.
   @param event The event object that contains information about the
   requested data.
   */
    loadData(event) {
        this.lastLazyLoad = event;
        const params = this.prepareParams(event);
        this.changeRef.markForCheck();
        this.query(event, params)
            .pipe(map((i) => this.mapper(i)))
            .subscribe((res) => {
            this.data.next(res.content);
            this.totalRecords = res.totalElements;
            this.loading = false;
            this.changeRef.markForCheck();
        });
    }
    /**
     * Creates Parameters for LazyLoading HttpRequest creating order functions
     * @param event
     */
    prepareParams(event) {
        let params = new HttpParams();
        const sortField = event.sortField;
        const order = (event.sortOrder || -1) === 1 ? 'ASC' : 'DESC';
        if (sortField)
            params = params.set('sort', `${sortField}-${order}`);
        if (this.options && this.options.switches) {
            this.options.switches.forEach((s) => (params = this.checkSwitch(params, s)));
        }
        return params;
    }
    /**
     * Checks a switch in the options and returns a HttpParams Object in the following steps
     * If switch key is empty: Input HttpParams
     * Else If switch always property is true: HttpParams with key and checked value
     * Else If switch is checked: HttpParams with key and true value
     * @param params Existing HttpParams
     * @param s Switch Option
     */
    checkSwitch(params, s) {
        if (!s.key)
            return params;
        if (s.always)
            return params.set(s.key, s.checked);
        if (s.checked)
            return params.set(s.key, true);
        return params;
    }
    refresh() {
        if (this.lastLazyLoad !== undefined)
            this.loadData(this.lastLazyLoad);
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { LazyDataPaging };
